import { create } from 'zustand'
import type { CameraPresetId, CardsMode, LayerFlags, PlaybackPhase } from './types'
import { POSES } from './poses/library'

export interface AppState {
  poseId: string
  search: string
  categoryFilter: string | 'all'
  playing: boolean
  speed: number
  holdPose: boolean
  elapsedMs: number
  phase: PlaybackPhase
  poseWeight: number
  cardsMode: CardsMode
  layers: LayerFlags
  cameraPreset: CameraPresetId
  showGrid: boolean
  showAbout: boolean
  modelReady: boolean
  modelError: string | null
  loadProgress: number
  recording: boolean
  recordProgress: number
  controlsLocked: boolean
  setPoseId: (id: string) => void
  setSearch: (q: string) => void
  setCategoryFilter: (c: string | 'all') => void
  setPlaying: (v: boolean) => void
  setSpeed: (v: number) => void
  setHoldPose: (v: boolean) => void
  setElapsedMs: (v: number) => void
  setPhase: (p: PlaybackPhase, weight: number) => void
  setCardsMode: (m: CardsMode) => void
  setLayer: (key: keyof LayerFlags, v: boolean) => void
  setCameraPreset: (id: CameraPresetId) => void
  setShowGrid: (v: boolean) => void
  setShowAbout: (v: boolean) => void
  setModelReady: (v: boolean) => void
  setModelError: (e: string | null) => void
  setLoadProgress: (n: number) => void
  setRecording: (v: boolean) => void
  setRecordProgress: (n: number) => void
  setControlsLocked: (v: boolean) => void
  restart: () => void
}

const defaultLayers: LayerFlags = {
  muscles: true,
  skeleton: false,
  organs: false,
  circulation: false,
  respiratory: false,
  spine: false,
  pelvic: false,
}

export const useAppStore = create<AppState>((set) => ({
  poseId: POSES[0]?.id ?? 'mountain',
  search: '',
  categoryFilter: 'all',
  playing: true,
  speed: 1,
  holdPose: false,
  elapsedMs: 0,
  phase: 'entry',
  poseWeight: 0,
  cardsMode: 'working-stabilizing',
  layers: defaultLayers,
  cameraPreset: 'front',
  showGrid: true,
  showAbout: false,
  modelReady: false,
  modelError: null,
  loadProgress: 0,
  recording: false,
  recordProgress: 0,
  controlsLocked: false,
  setPoseId: (id) => set({ poseId: id, elapsedMs: 0, phase: 'entry', poseWeight: 0 }),
  setSearch: (search) => set({ search }),
  setCategoryFilter: (categoryFilter) => set({ categoryFilter }),
  setPlaying: (playing) => set({ playing }),
  setSpeed: (speed) => set({ speed }),
  setHoldPose: (holdPose) => set({ holdPose }),
  setElapsedMs: (elapsedMs) => set({ elapsedMs }),
  setPhase: (phase, poseWeight) => set({ phase, poseWeight }),
  setCardsMode: (cardsMode) => set({ cardsMode }),
  setLayer: (key, v) => set((s) => ({ layers: { ...s.layers, [key]: v } })),
  setCameraPreset: (cameraPreset) => set({ cameraPreset }),
  setShowGrid: (showGrid) => set({ showGrid }),
  setShowAbout: (showAbout) => set({ showAbout }),
  setModelReady: (modelReady) => set({ modelReady }),
  setModelError: (modelError) => set({ modelError }),
  setLoadProgress: (loadProgress) => set({ loadProgress }),
  setRecording: (recording) => set({ recording }),
  setRecordProgress: (recordProgress) => set({ recordProgress }),
  setControlsLocked: (controlsLocked) => set({ controlsLocked }),
  restart: () => set({ elapsedMs: 0, phase: 'entry', poseWeight: 0, playing: true }),
}))
